import { Program, TClientId, TProgramId, TReportingPeriodTypeId } from '/src/lib/models';
import { FloatString } from '/src/lib/types';

export type TIncentiveStatementId = number;
export type TIncentiveStatementReportingPeriodId = number;

/** Incentive statement as returned in the reporting period list (raw model) */
export interface IncentiveStatementListItem {
  id: TIncentiveStatementId;
  program_id: TProgramId;
  program?: Program;
  statement_date: Date;
}

export interface IncentiveStatementReportingPeriod {
  id: TIncentiveStatementReportingPeriodId;
  client_id: TClientId;
  reporting_period_type_id: TReportingPeriodTypeId;
  start_reporting_quarter: Date;
  end_reporting_quarter: Date;
  created_at: Date;
  updated_at: Date;
  incentive_statements: IncentiveStatementListItem[];
}

/** Shared financial fields present at every level */
export interface IncentiveStatementFinancials {
  total_kwh: number;
  credits: FloatString;
  total_value: FloatString;
  rec_cost: FloatString;
  broker_cost: FloatString;
  taxes_on_credit_sale?: FloatString;
  taxes_on_services?: FloatString;
  fuse_service_fee: FloatString;
  net_incentive: FloatString;
}

/** Equipment category row within a facility */
export interface IncentiveStatementEquipmentCategory extends IncentiveStatementFinancials {
  name: string;
  credit_price: FloatString;
}

/** Facility row within a program */
export interface IncentiveStatementFacility extends IncentiveStatementFinancials {
  address: string;
  credit_price: FloatString;
  equipment_categories: IncentiveStatementEquipmentCategory[];
}

/** Single program within a response */
export interface IncentiveStatementProgram extends IncentiveStatementFinancials {
  name: string;
  credit_price: FloatString;
  facilities: IncentiveStatementFacility[];
}

/** Response shape for both endpoints (single statement and reporting period) */
export interface IncentiveStatementResponse extends IncentiveStatementFinancials {
  client: string;
  reporting_period: string;
  programs: IncentiveStatementProgram[];
}

/** Normalize response into programs array + totals */
export const normalizeIncentiveStatementResponse = (response: IncentiveStatementResponse) => {
  return {
    programs: response.programs,
    totals: {
      total_kwh: response.total_kwh,
      credits: response.credits,
      total_value: response.total_value,
      rec_cost: response.rec_cost,
      broker_cost: response.broker_cost,
      taxes_on_credit_sale: response.taxes_on_credit_sale,
      taxes_on_services: response.taxes_on_services,
      fuse_service_fee: response.fuse_service_fee,
      net_incentive: response.net_incentive,
    },
    client_name: response.client,
    reporting_period: response.reporting_period,
  };
};
